<script>
	import {onDestroy} from 'svelte';
	import API from '../../States/API';
	import Data from '../../States/Data';
	import Helper from '../../Models/Helper';
	import Scripture from '../../Models/Scripture';
	import Session from '../../States/Session';
	import Slider from '../../States/Slider';

	export let index = 0;

	/* Days per block in the "alle dagen" grid, purely for orientation. */
	const BLOCK = 30;

	/* How far to look past a bridged verse for one that carries text. */
	const VERSE_LOOKAHEAD = 4;

	let properties = {};
	let plan = null;
	let active = false;
	let content = {};
	let grid = {};
	let day_index = 0;
	let days_open = false;
	/* Passage code -> {number, text} for the opening verse, or null while pending. */
	let previews = {};

	/*
	 * Everything derived from progress goes through these reactive statements. The
	 * previous version called plain functions from the template, which read
	 * $Session inside a function body — a dependency the compiler cannot see, so
	 * the progress bar, the day counter and the strike-through never updated when
	 * a passage was ticked. Naming $Session here is what makes it work.
	 */
	$: done = new Set((plan && $Session.plans && $Session.plans[plan.code]) || []);
	$: day_total = plan ? plan.steps.length : 0;

	/* Read count per day; feeds both the counter and the grid colours. */
	$: day_read = plan ? plan.steps.map(step => step.passages.filter(passage => done.has(passage.code)).length) : [];
	$: read_total = day_read.reduce((total, count) => total + count, 0);

	/*
	 * Counted through the plan rather than from done.size, because a session can
	 * still hold passage codes from an earlier version of the schedule — those
	 * would push the total past 100%.
	 */
	$: passage_total = plan ? plan.steps.reduce((total, step) => total + step.passages.length, 0) : 0;
	$: percent = passage_total ? Math.round((read_total / passage_total) * 100) : 0;
	$: finished = (passage_total > 0) && (read_total >= passage_total);

	$: day = (plan && plan.steps[day_index]) || null;
	$: day_unread = day ? day.passages.filter(passage => !done.has(passage.code)) : [];
	$: blocks = buildBlocks(day_total);

	/* Only the day on screen is fetched, and only once. */
	$: loadPreviews(day);

	function firstIncompleteDay(steps, read) {
		for (let number = 0; number < steps.length; number++)
			if (steps[number].passages.some(passage => !read.has(passage.code)))
				return number;

		/* Everything read — stay on the last day. */
		return steps.length ? (steps.length - 1) : 0;
	}

	function buildBlocks(total) {
		const list = [];
		for (let start = 1; start <= total; start += BLOCK)
			list.push({start, end: Math.min(total, start + BLOCK - 1)});
		return list;
	}

	async function load(code, day) {
		plan = null;
		days_open = false;
		previews = {};
		const response = await API.plan(code);
		if (response.success) {
			plan = response.plan;
			Helper.title(plan.title);
			if (day) {
				/* Gevraagd om een dag, geklemd binnen het plan. */
				day_index = dayIndexFor(day, plan.steps.length);
			}
			else {
				/* Open on the day the reader is actually at. */
				const read = new Set(($Session.plans && $Session.plans[code]) || []);
				day_index = firstIncompleteDay(plan.steps, read);
			}
		}
	}

	/*
	 * verseByChapter() leaves <p class="p"> and <br/> in place, which a preview
	 * wants neither of. Everything else it already stripped, so what comes out
	 * here is plain text and needs no {@html}.
	 */
	function cleanVerse(html) {
		return String(html || '')
			.replace(/<br\s*\/?>/gi, ' ')
			.replace(/<[^>]*>/g, '')
			.replace(/\s+/g, ' ')
			.trim();
	}

	/*
	 * A bridged verse carries only a "+" marker — its words live in the verse
	 * after it. 1 Samuel 18:1, Lukas 3:1 and six others in these plans do this.
	 */
	function usableVerse(text) {
		return text.replace(/[^0-9A-Za-zÀ-ÿ]/g, '').length >= 3;
	}

	/*
	 * Loads the opening verse of every reading of the given day. Costs two or
	 * three chapter files, cached per uri under the very key Chapter.svelte reads
	 * — so this also warms the chapter the reader opens next.
	 */
	async function loadPreviews(step) {
		if (!step) return;

		for (const passage of step.passages) {
			/* Claim the slot first, so flicking through days cannot fetch twice. */
			if (!Helper.undefined(previews[passage.code])) continue;
			previews[passage.code] = null;

			const info = Scripture.parsePassage(passage.code);
			if (!info) continue;

			const chapter = await Data.relative(`books/${info.book}/${info.location}`);
			if (!chapter || !chapter.html) continue;

			const first = info.verses.length ? info.verses[0] : 1;
			for (let number = first; number < (first + VERSE_LOOKAHEAD); number++) {
				const text = cleanVerse(Scripture.verseByChapter(chapter.html, number));
				if (usableVerse(text)) {
					previews[passage.code] = {number, text};
					break;
				}
			}

			previews = previews;
		}
	}

	/*
	 * Het totaal komt als argument mee: day_total is een reactieve waarde, en die
	 * loopt een cyclus achter op de `plan =` in load(). Met day_total zou een dag
	 * die tijdens het laden gevraagd wordt tegen een totaal van 0 geklemd worden en
	 * dus altijd op dag 1 uitkomen.
	 */
	function dayIndexFor(number, total) {
		return Math.max(1, Math.min(total, number)) - 1;
	}

	function toDay(number) {
		day_index = dayIndexFor(number, day_total);
	}

	function toggleDays() {
		days_open = !days_open;
		if (days_open) setTimeout(scrollToCurrentDay, 50);
	}

	function pickDay(number) {
		toDay(number);
		days_open = false;
		jQuery(content).animate({scrollTop: 0}, 1000 * .25);
	}

	function togglePassage(code) {
		Session.togglePlanPassage(plan.code, code);
	}

	function openPassage(passage) {
		Slider.passage(passage.code, plan.code);
	}

	function readNext() {
		if (day_unread.length)
			Slider.passage(day_unread[0].code, plan.code);
	}

	/* Same offset maths as Read/Index.svelte uses to reveal its active item. */
	function scrollToCurrentDay() {
		const base = jQuery(content);
		const element = jQuery(grid).find('a.current');
		if (!base.length || !element.length) return;

		let top = element.offset().top - base.offset().top + base.scrollTop();
		top = ((top - 60) > 0) ? (top - 60) : 0;
		base.animate({scrollTop: top}, 1000 * .3);
	}

	function updateSlider(slides) {
		active = Slider.check(index, 'plan', properties);
		if (active) {
			const new_props = Slider.properties(index);
			active = new_props.active;
			const code_changed = new_props.code !== properties.code;
			const day_changed = new_props.day !== properties.day;
			properties = new_props;
			if (active && (code_changed || !plan)) load(properties.code, properties.day);
			/* Het plan staat er al en alleen de gevraagde dag verschilt — via de balk
			   gebeurt dat niet (dit onderdeel is dan net weggegooid), maar wel als de
			   lezer met de terugknop op een dag uitkomt. */
			else if (active && day_changed && properties.day) toDay(properties.day);
		}
	}

	const unsubscribe = Slider.subscribe(updateSlider);
	onDestroy(unsubscribe);
</script>

<style>
	/*
	 * Het achtergrondbeeld van de pagina zacht laten doorschemeren, zoals het
	 * zoekscherm dat doet (Search.svelte). Het laagje zit op de box; div.content
	 * moet transparant, anders legt box.css er een dekkende laag bovenop.
	 *
	 * Op de eigen klasse van de box, niet op div.box.feature: dan winnen deze
	 * regels op specificiteit in plaats van op de laadvolgorde van bundle.css.
	 */
	div.box.plan {
		background: var(--box-feature-background-transparent);
	}

	div.box.plan div.content {
		background: transparent;
	}

	/*
	 * Sits under the progress bar. No longer adjacent to the h1, so box.css's
	 * `h1 + p:not(.plain)` rule (1.5em italic Bitter, 4rem below) no longer
	 * applies — which is what we want here: this is a working screen.
	 */
	p.intro {
		color: var(--secondary-color);
		line-height: 1.6em;
		margin: 0 0 2.25em 0;
	}

	div.progress-wrap {
		margin-top: 1.75em;
		margin-bottom: 1.25em;
	}

	/* Without an intro the bar would sit too close to the day; make up the rest. */
	div.progress-wrap + div.day-nav {
		margin-top: 1.25em;
	}

	div.progress-bar {
		height: .7em;
		border-radius: .35em;
		background: var(--tertiary-background-light);
		overflow: hidden;
	}

	div.progress-bar div.fill {
		height: 100%;
		border-radius: .35em;
		background: var(--tertiary-color);
		transition: width .3s ease-out;
	}

	/* Keep a dot visible once anything is read, as div.percentage does. */
	div.progress-bar div.fill.any {
		min-width: .7em;
	}

	div.day-nav {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: .75em;
	}

	div.day-nav div.step {
		width: 2.5em;
		text-align: center;
		font-size: 1.1em;
		color: var(--tertiary-color);
		cursor: pointer;
	}

	div.day-nav div.step.disabled {
		opacity: .2;
		cursor: default;
	}

	div.day-nav div.label {
		flex: 1;
		text-align: center;
		font-family: Bitter, serif;
		font-size: 1.5em;
		color: var(--primary-color);
	}

	/* div.list.solid gives the row its card shape and places the chevron. */
	div.list :global(.item) {
		display: flex;
		align-items: flex-start;
		padding-right: 2.5em;
	}

	div.list :global(.item input[type="checkbox"]) {
		flex-shrink: 0;
		width: 1.1em;
		height: 1.1em;
		/* Sits on the title's line rather than the middle of the whole row. */
		margin-top: .3em;
		margin-right: .9em;
		accent-color: var(--tertiary-color);
		cursor: pointer;
	}

	div.list :global(.item div.text) {
		flex: 1;
		cursor: pointer;
	}

	div.list :global(.item div.title) {
		display: block;
		color: var(--primary-color);
		line-height: 1.5em;
	}

	/* The strike-through belongs to the title, never to the verse. */
	div.list :global(.item.read div.title) {
		color: var(--secondary-color);
		text-decoration: line-through;
		opacity: .6;
	}

	div.list :global(.item div.verse) {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		margin-top: .1em;
		font-size: .95em;
		font-weight: 400;
		line-height: 1.45em;
		color: var(--secondary-color);
	}

	div.list :global(.item div.verse span.number) {
		font-weight: 700;
		opacity: .5;
		margin-right: .15em;
	}

	div.list :global(.item i) {
		color: var(--tertiary-color);
	}

	div.list :global(.item.read i) {
		opacity: .4;
	}

	div.read-next {
		margin-top: 1.25em;
		margin-bottom: 2em;
	}

	div.all-done {
		text-align: center;
		padding: 1.5em 0 2em 0;
		color: var(--secondary-color);
	}

	div.all-done i {
		font-size: 2em;
		display: block;
		margin-bottom: .5em;
		color: var(--tertiary-color);
	}

	div.days {
		margin-bottom: 2em;
	}

	/* No opacity: at .6 this was too faint to read as a heading. */
	div.days div.block {
		font-size: .75em;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: .05em;
		color: var(--secondary-color);
		margin-bottom: .6em;
	}

	/* Same chip as the chapter picker in Read/Chapters.svelte. */
	div.days span {
		display: inline-block;
		font-family: Bitter, serif;
		font-size: .8em;
		font-weight: 600;
		line-height: 2em;
		color: var(--primary-color);
		width: 2.3em;
		background: var(--secondary-background-light);
		text-align: center;
		border-radius: .3em;
		padding: .1em 0;
		margin: 0 .75em .75em 0;
		cursor: pointer;
	}

	div.days span.part {
		color: var(--tertiary-color);
	}

	/*
	 * Filled, the way span.current inverts: the accent as the fill and the page
	 * colour as the text. --background rather than a literal white, because in the
	 * dark themes --tertiary-color is itself a light tone — white on white there.
	 */
	div.days span.read {
		color: var(--background);
		background: var(--tertiary-color);
	}

	div.days span.current {
		color: var(--background);
		background: var(--primary-color);
	}

	div.days div.group {
		margin-bottom: 1.5em;
	}
</style>

<div class="box feature plan" class:active>
	{#if plan}
		<div class="menu">
			<div class="begin">
				<div class="item" on:click="{event => Slider.route('plans')}">
					<i class="fas fa-chevron-left"></i>
				</div>
				<div class="title">{plan.title}</div>
			</div>
			<div class="end">
				<div class="item" on:click="{event => Slider.close()}">
					<i class="fas fa-times"></i>
				</div>
			</div>
		</div>
		<div class="content" bind:this="{content}">
			<h1>{plan.title}</h1>

			<div class="progress-wrap">
				<div class="progress-bar">
					<div class="fill" class:any="{read_total > 0}" style="width: {percent}%;"></div>
				</div>
			</div>

			{#if plan.intro}
				<p class="intro">{plan.intro}</p>
			{/if}

			{#if day}
				<div class="day-nav">
					<div class="step" class:disabled="{day_index === 0}" on:click="{event => toDay(day_index)}">
						{#if day_index > 0}
							<i class="fas fa-chevron-left"></i>
						{/if}
					</div>
					<div class="label">{day.label}</div>
					<div class="step" class:disabled="{day_index >= (day_total - 1)}" on:click="{event => toDay(day_index + 2)}">
						{#if day_index < (day_total - 1)}
							<i class="fas fa-chevron-right"></i>
						{/if}
					</div>
				</div>

				<div class="solid list">
					{#each day.passages as passage (passage.code)}
						{@const read = done.has(passage.code)}
						{@const preview = previews[passage.code]}
						<div class="item" class:read>
							<input
								type="checkbox"
								checked="{read}"
								on:change="{event => { event.stopPropagation(); togglePassage(passage.code); }}"
							/>
							<!--
								Divs rather than spans: list.css ends with
								`div.list.solid > .item > div > span { background: … }`,
								which would put a grey band behind both lines.
							-->
							<div class="text" on:click="{event => openPassage(passage)}">
								<div class="title">{passage.title}</div>
								{#if preview}
									<div class="verse">
										<span class="number">{preview.number}</span> {preview.text}
									</div>
								{/if}
							</div>
							<i class="fas fa-chevron-right" on:click="{event => openPassage(passage)}"></i>
						</div>
					{/each}
				</div>

				<!--
					A finished day is by definition not the current day, so the
					"terug naar dag N" link above already offers the way onward.
				-->
				<div class="read-next">
					{#if day_unread.length}
						<div class="button attention" on:click="{readNext}">
							<i class="fad fa-book-open"></i> Verder lezen
						</div>
					{:else if finished}
						<div class="all-done">
							<i class="fad fa-check-circle"></i>
							Gefeliciteerd! Je hebt dit leesplan afgerond.
						</div>
					{/if}
				</div>
			{/if}

			<div class="button tertiary" on:click="{toggleDays}">
				<i class="fad fa-list-ol"></i> Alle dagen
				<i class="fas fa-chevron-{days_open ? 'up' : 'down'}"></i>
			</div>
			{#if days_open}
				<div class="days" bind:this="{grid}">
					{#each blocks as block}
						<div class="group">
							<div class="block">Dag {block.start} – {block.end}</div>
							{#each Array(block.end - block.start + 1) as _, offset}
								{@const number = block.start + offset}
								{@const read = day_read[number - 1]}
								{@const total = plan.steps[number - 1].passages.length}
								<span
									class:read="{read === total}"
									class:part="{read > 0 && read < total}"
									class:current="{number === (day_index + 1)}"
									on:click="{event => pickDay(number)}"
								>{number}</span>
							{/each}
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{:else}
		<div class="menu">
			<div class="begin">
				<div class="item" on:click="{event => Slider.route('plans')}">
					<i class="fas fa-chevron-left"></i>
				</div>
			</div>
			<div class="end">
				<div class="item" on:click="{event => Slider.close()}">
					<i class="fas fa-times"></i>
				</div>
			</div>
		</div>
		<div class="content">
			<div class="notice">
				<i class="fad fa-spinner fa-spin"></i> Leesplan laden…
			</div>
		</div>
	{/if}
</div>
